import { useState, useEffect, useMemo } from 'react';
import { Check, Search, Loader2, Ban, MessageSquareWarning, HelpCircle, Utensils, X } from 'lucide-react';
import TypBadge from '../../../components/receptura/TypBadge';
import { api, AKCENT as SECTION_COLOR } from '../../../lib/spizirnaUi';

const bezDiakritiky = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

/** Nad tímhle skóre je návrh jistota; pod ním je to tip k prohlédnutí. */
const JISTY = 88;

const DUVODY = {
    'presna-shoda': 'název i varianta sedí',
    'nazev+slovnik': 'varianta přes slovník',
    'nazev+varianta': 'jediná varianta pod tím názvem',
    'podobnost': 'podobný název',
    'chybi-varianta': 'variantu neznáme',
    'historicky-odhad': 'starý odhad, neověřený',
};

/**
 * Detail vybraného názvu — **druhý sloupec, ne překryvný inspektor**.
 *
 * Inspektor podle §1f je na moduly, kde se detail otevře občas. Tady je to
 * naopak pracovní plocha: projde se jím několik set názvů za sebou a na
 * velkém monitoru není důvod kvůli tomu překrývat seznam. Rozvržení proto
 * jede podle §1 — seznam se zúží a detail je plnohodnotný sloupec vedle něj.
 *
 * Čtyři odpovědi, ne jedna. Dřív šlo říct jen „tohle je receptura X" nebo
 * „ignorovat", a tím se ztrácela informace: `Bez přílohy` (180× za 90 dní)
 * není zboží ani odpad, je to **pokyn pro kuchaře** — a jako `ignorovat`
 * by zmizel a kuchař by přílohu přidal.
 */
export default function DetailParovani({ radek, isDark, isMobile, styly, onZavrit, onUlozit, onPotvrdit }) {
    const { secondaryColor, mutedColor, inputStyle } = styly;
    const [kandidati, setKandidati] = useState(null);
    const [hledani, setHledani] = useState('');
    const [vsechny, setVsechny] = useState([]);

    useEffect(() => {
        setKandidati(null);
        setHledani('');
        api(`/api/objedname/mapping/${radek.id}/navrhy`).then(setKandidati).catch(() => setKandidati({ kandidati: [] }));
    }, [radek.id]);

    useEffect(() => {
        if (vsechny.length) return;
        api('/api/objedname/mapping/ciselniky').then((c) => setVsechny(c.recepty || [])).catch(() => {});
    }, [vsechny.length]);

    // Dokud se nehledá, ukazují se kandidáti od párovače se skóre. Jakmile
    // člověk začne psát, jsou to jeho kritéria — a tam skóre nic neříká.
    const seznam = useMemo(() => {
        const q = bezDiakritiky(hledani.trim());
        if (!q) return kandidati?.kandidati ?? [];
        return vsechny
            .filter((r) => bezDiakritiky(`${r.kod} ${r.nazev} ${r.varianta || ''}`).includes(q))
            .slice(0, 40)
            .map((r) => ({ recept_id: r.id, kod: r.kod, nazev: r.nazev, varianta: r.varianta, typ: r.typ, skore: null }));
    }, [hledani, kandidati, vsechny]);

    const navrh = radek.navrh;
    const jisty = (navrh?.skore ?? 0) >= JISTY;

    return (
        <div className="rounded-xl overflow-hidden flex flex-col flex-1"
            style={{ ...styly.cardStyle, minHeight: 0 }}>
            <div className="flex items-center gap-2 px-4 py-3 flex-shrink-0"
                style={{
                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-default)'}`,
                    borderTop: `2px solid ${SECTION_COLOR}`,
                }}>
                <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>
                        {radek.nazev}
                    </div>
                    {radek.skupina_obj && (
                        <div className="text-[11px] truncate" style={{ color: mutedColor }}>
                            {radek.skupina_obj}
                        </div>
                    )}
                </div>
                <button onClick={onZavrit}
                    className="grid place-items-center flex-shrink-0"
                    style={{
                        width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
                        background: 'transparent', border: `1px solid ${mutedColor}33`, color: mutedColor,
                    }}
                    title="Zavřít detail">
                    <X size={15} />
                </button>
            </div>

            <div className="flex flex-col" style={{ padding: 16, gap: 14, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                <Rozbor radek={radek} mutedColor={mutedColor} secondaryColor={secondaryColor} />

                {navrh?.recept && (
                    <div className="p-3 rounded-lg"
                        style={{
                            background: jisty ? 'rgba(16,185,129,0.09)' : 'rgba(245,158,11,0.09)',
                            border: `1px solid ${jisty ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.32)'}`,
                        }}>
                        <div className="text-[10px] font-bold uppercase tracking-wider mb-1"
                            style={{ color: jisty ? '#10B981' : SECTION_COLOR }}>
                            Návrh · {navrh.skore} % · {DUVODY[navrh.duvod] ?? navrh.duvod}
                        </div>
                        <div className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                            {navrh.recept.nazev}
                            {navrh.recept.varianta && (
                                <span style={{ color: SECTION_COLOR, marginLeft: 5, fontSize: 11 }}>
                                    ({navrh.recept.varianta})
                                </span>
                            )}
                        </div>
                        {/* Slabý návrh nedostane zelené tlačítko. Zelená na téhle
                            obrazovce znamená „tohle je ono"; u šedesáti procent
                            to není pravda a hlavní akce má být prohlédnout
                            kandidáty níž, ne odklepnout. */}
                        <button onClick={onPotvrdit}
                            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] font-bold rounded-lg"
                            style={jisty
                                ? { height: 34, cursor: 'pointer', background: '#10B981', color: '#062017', border: 'none' }
                                : {
                                    height: 34, cursor: 'pointer', background: 'transparent',
                                    border: `1px solid ${SECTION_COLOR}66`, color: SECTION_COLOR,
                                }}>
                            <Check size={14} strokeWidth={3} />
                            {jisty ? 'Potvrdit tenhle' : 'Přesto potvrdit'}
                        </button>
                    </div>
                )}

                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: mutedColor }}>
                        {hledani ? 'Výsledky hledání' : 'Nejbližší receptury'}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: mutedColor }} />
                        <input placeholder="Hledat v receptáři…" value={hledani}
                            onChange={(e) => setHledani(e.target.value)}
                            style={{ ...inputStyle, paddingLeft: 28, fontSize: 12 }} />
                    </div>

                    <div className="mt-1.5 rounded-lg overflow-hidden" style={{ border: `1px solid ${mutedColor}26` }}>
                        {kandidati === null ? (
                            <div className="flex justify-center py-5">
                                <Loader2 size={16} className="animate-spin" style={{ color: mutedColor }} />
                            </div>
                        ) : seznam.length === 0 ? (
                            <p className="text-[11.5px] text-center py-4" style={{ color: mutedColor }}>
                                Nic nenalezeno.
                            </p>
                        ) : (
                            <div style={{ maxHeight: 230, overflowY: 'auto' }}>
                                {seznam.map((r) => (
                                    <button key={r.recept_id}
                                        onClick={() => onUlozit({ cilovy_typ: 'recept', recept_id: r.recept_id })}
                                        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left"
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                                        <TypBadge typ={r.typ} />
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-[12px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                                {r.nazev}
                                                {r.varianta && (
                                                    <span style={{ color: SECTION_COLOR, marginLeft: 4, fontSize: 10, fontWeight: 600 }}>
                                                        ({r.varianta})
                                                    </span>
                                                )}
                                            </span>
                                            <span className="block text-[9.5px]" style={{ color: mutedColor }}>{r.kod}</span>
                                        </span>
                                        {r.skore != null && (
                                            <span className="text-[10px] tnum flex-shrink-0" style={{ color: mutedColor }}>
                                                {r.skore} %
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: mutedColor }}>
                        Není to receptura?
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Volba Ikona={MessageSquareWarning} barva="#F59E0B"
                            titulek="Je to pokyn do poznámky"
                            popis="„Bez přílohy“ není zboží. Text se připíše k poznámce jídla."
                            onClick={() => onUlozit({ cilovy_typ: 'poznamka' })} />
                        <Volba Ikona={HelpCircle} barva="#EF4444"
                            titulek="Protějšek u nás chybí"
                            popis="Založit recepturu. Zůstane ve frontě, dokud nevznikne."
                            onClick={() => onUlozit({ stav: 'chybi-protejsek' })} />
                        <Volba Ikona={Ban} barva="#8B8B9A"
                            titulek="Ignorovat"
                            popis="Sleva, obal a podobné položky, co kuchyň nedělá."
                            onClick={() => onUlozit({ cilovy_typ: 'ignorovat' })} />
                    </div>
                </div>

                {radek.typ === 'pridavek' && (
                    <div className="p-3 rounded-lg" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${mutedColor}26` }}>
                        <div className="flex items-start gap-2">
                            <Utensils size={13} style={{ color: SECTION_COLOR, marginTop: 2, flexShrink: 0 }} />
                            <div>
                                <div className="text-[11.5px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                                    Samostatná práce v kuchyni?
                                </div>
                                <p className="text-[10.5px] mt-0.5 leading-snug" style={{ color: mutedColor }}>
                                    Hranolky někdo usmaží a odškrtne zvlášť — vlastní řádek na kartě.
                                    Čedarová omáčka se uvaří s burgerem.
                                </p>
                                <div className="flex gap-1.5 mt-2">
                                    <Prepinac aktivni={radek.kuchyn_samostatne === true} barva="#10B981"
                                        onClick={() => onUlozit({ kuchyn_samostatne: true })}>vlastní řádek</Prepinac>
                                    <Prepinac aktivni={radek.kuchyn_samostatne === false} barva={mutedColor}
                                        onClick={() => onUlozit({ kuchyn_samostatne: false })}>úprava jídla</Prepinac>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/** Jak se název rozebral — ať je vidět, podle čeho se vlastně porovnávalo. */
function Rozbor({ radek, mutedColor, secondaryColor }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: mutedColor }}>
                Z Objedname
            </div>
            <div className="text-[12px] font-mono px-2 py-1.5 rounded"
                style={{ background: 'rgba(160,160,180,0.1)', color: secondaryColor }}>
                {radek.externi_nazev}
            </div>
            <div className="flex gap-4 mt-1.5 text-[11px] flex-wrap">
                {radek.poradi && (
                    <span style={{ color: mutedColor }}>
                        pořadí <b style={{ color: 'var(--text-primary)' }}>{radek.poradi}</b>
                    </span>
                )}
                <span style={{ color: mutedColor }}>
                    název <b style={{ color: 'var(--text-primary)' }}>{radek.nazev}</b>
                </span>
                <span style={{ color: mutedColor }}>
                    varianta <b style={{ color: 'var(--text-primary)' }}>{radek.varianta || '—'}</b>
                </span>
            </div>
            <div className="text-[11px] mt-1" style={{ color: mutedColor }}>
                {radek.kusu}× za 90 dní · {Math.round(radek.trzba).toLocaleString('cs-CZ')} Kč
                {radek.naposledy && ` · naposledy ${new Date(radek.naposledy).toLocaleDateString('cs-CZ')}`}
            </div>
        </div>
    );
}

function Volba({ Ikona, barva, titulek, popis, onClick }) {
    return (
        <button onClick={onClick}
            className="flex items-start gap-2 p-2 rounded-lg text-left"
            style={{ background: 'transparent', border: `1px solid ${barva}33`, cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = `${barva}12`)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
            <Ikona size={14} style={{ color: barva, marginTop: 1, flexShrink: 0 }} />
            <span className="min-w-0">
                <span className="block text-[12px] font-semibold" style={{ color: 'var(--text-primary)' }}>{titulek}</span>
                <span className="block text-[10.5px] leading-snug" style={{ color: 'var(--text-muted, #8B8B9A)' }}>{popis}</span>
            </span>
        </button>
    );
}

function Prepinac({ aktivni, barva, onClick, children }) {
    return (
        <button onClick={onClick}
            className="text-[10.5px] font-semibold px-2 py-1 rounded"
            style={{
                cursor: 'pointer',
                background: aktivni ? `${barva}22` : 'transparent',
                border: `1px solid ${aktivni ? barva : 'rgba(160,160,180,0.25)'}`,
                color: aktivni ? barva : 'var(--text-muted, #8B8B9A)',
            }}>
            {children}
        </button>
    );
}
